import React, { useState } from 'react';
import styled from 'styled-components';
import { DateRange } from 'react-day-picker';
import moment, { Moment } from 'moment';
import { DateRangeInputs } from '../DateRangeInputs';
import { DayPickerPopover } from '../DayPickerPopover';

interface DateRangeSelectorProps {
  dateFormat?: string;
  disabled?: boolean;
  endDate: Date | null;
  isDayHighlighted: (date: Date) => boolean;
  onDateChange: ({
    startDate,
    endDate,
  }: {
    startDate: Moment | null;
    endDate: Moment | null;
  }) => void;
  startDate: Date | null;
  onPrevMonthClick?: (date: Moment) => void;
  onNextMonthClick?: (date: Moment) => void;
  numberOfMonthsToDisplay?: number;
}

const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({
  dateFormat,
  disabled = false,
  endDate,
  isDayHighlighted,
  onDateChange,
  startDate,
  onPrevMonthClick,
  onNextMonthClick,
  numberOfMonthsToDisplay = 2,
}) => {
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const handleCalendarOpen = () => {
    if (disabled) return;
    setIsCalendarOpen(true);
  };

  const handleDateChange = (range: DateRange) => {
    onDateChange({
      startDate: range?.from ? moment(range?.from) : null,
      endDate: range?.to ? moment(range?.to) : null,
    });
  };

  const clearDates = () => {
    onDateChange({ startDate: null, endDate: null });
  };

  const handleClose = () => {
    setIsCalendarOpen(false);
  };

  return (
    <DateRangeSelectorContainer>
      <DayPickerPopover
        mode="range"
        asChild
        required
        open={isCalendarOpen}
        numberOfMonths={numberOfMonthsToDisplay}
        min={1}
        sideOffset={10}
        align="end"
        onPrevClick={(date) => onPrevMonthClick?.(moment(date))}
        onNextClick={(date) => onNextMonthClick?.(moment(date))}
        onClearButtonClick={clearDates}
        onCancelButtonClick={handleClose}
        onApplyButtonClick={handleClose}
        onOpenChange={setIsCalendarOpen}
        onSelect={handleDateChange}
        selected={{
          from: startDate ?? undefined,
          to: endDate ?? undefined,
        }}
        modifiers={{
          disabled: (date) => !isDayHighlighted(date),
        }}
        trigger={
          <div>
            <DateRangeInputs
              startDate={startDate}
              endDate={endDate}
              onStartDateClick={handleCalendarOpen}
              onEndDateClick={handleCalendarOpen}
              disabled={disabled}
              dateFormat={dateFormat}
              firstPlaceholder="arrival"
              secondPlaceholder="departure"
            />
          </div>
        }
      />
    </DateRangeSelectorContainer>
  );
};

const DateRangeSelectorContainer = styled.div`
  position: relative;
  width: 100%;
`;

export default DateRangeSelector;
